import { useCallback, useEffect, useState } from "react";
import { Copy, MagnifyingGlass, PencilSimple, Plus, ChartBar, Trash, Prohibit, WhatsappLogo, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CampaignWizard from "../components/marketing/CampaignWizard";
import CampaignStats from "../components/marketing/CampaignStats";
import WaTemplates from "../components/WaTemplates";

const CHANNELS = [
  { key: "", label: "Tutti" },
  { key: "email", label: "Email" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "sms", label: "SMS" },
  { key: "whatsapp_api", label: "WhatsApp API" },
];

const STATUSES = [
  { key: "", label: "Tutti gli stati" },
  { key: "bozza", label: "Bozza" },
  { key: "programmata", label: "Programmata" },
  { key: "in_corso", label: "In corso" },
  { key: "completata", label: "Completata" },
  { key: "annullata", label: "Annullata" },
];

const STATUS_STYLE = {
  bozza: "bg-amber-100 text-amber-800 border-amber-300",
  programmata: "bg-blue-100 text-blue-800 border-blue-300",
  in_corso: "bg-emerald-100 text-emerald-800 border-emerald-300",
  completata: "bg-zinc-100 text-zinc-600 border-zinc-300",
  annullata: "bg-rose-100 text-rose-700 border-rose-300",
};

export default function Marketing() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [campaigns, setCampaigns] = useState([]);
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [wizard, setWizard] = useState(null); // null | {campaign?}
  const [statsFor, setStatsFor] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (channel) params.set("channel", channel);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/marketing/campaigns?${params}`).then((r) => setCampaigns(r.data))
      .catch(() => {}).finally(() => setLoading(false));
  }, [channel, status, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // auto-refresh mentre ci sono invii in corso: l'utente vede l'avanzamento senza ricaricare
  useEffect(() => {
    if (!campaigns.some((c) => c.status === "in_corso")) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [campaigns, load]);

  const duplicate = async (id) => {
    try { await api.post(`/marketing/campaigns/${id}/duplicate`); toast.success("Campagna duplicata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/marketing/campaigns/${id}`); toast.success("Campagna eliminata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const annulla = async (id) => {
    try { await api.post(`/marketing/campaigns/${id}/annulla`); toast.success("Campagna annullata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : "—";

  return (
    <div className="space-y-4" data-testid="marketing-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Centro Campagne</h1>
          <p className="text-xs text-zinc-500">Campagne multicanale: Email, WhatsApp, SMS, WhatsApp API</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTemplates(true)} data-testid="btn-template-whatsapp"
              className="h-11 px-4 rounded-md bg-white border border-zinc-300 text-sm font-semibold text-zinc-700 flex items-center gap-2 hover:border-zinc-400 active:scale-95 transition">
              <WhatsappLogo size={16} weight="bold" /> Template WhatsApp
            </button>
            <button onClick={() => setWizard({})} data-testid="btn-nuova-campagna"
              className="h-11 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2 hover:bg-zinc-800 active:scale-95 transition">
              <Plus size={16} weight="bold" /> Campagna
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca campagna per nome..."
            data-testid="campaign-search"
            className="w-full h-11 pl-9 pr-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="filter-channels">
          {CHANNELS.map((c) => (
            <button key={c.key} onClick={() => setChannel(c.key)} data-testid={`fchannel-${c.key || "tutti"}`}
              className={`shrink-0 h-9 px-3 rounded-full text-xs font-semibold border transition ${channel === c.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="filter-statuses">
          {STATUSES.map((s) => (
            <button key={s.key} onClick={() => setStatus(s.key)} data-testid={`fstatus-${s.key || "tutti"}`}
              className={`shrink-0 h-8 px-3 rounded-full text-[11px] font-semibold border transition ${status === s.key ? "bg-zinc-700 text-white border-zinc-700" : "bg-white text-zinc-500 border-zinc-200"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2" data-testid="campaigns-list">
        {loading && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
        {!loading && campaigns.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg py-10 text-center text-sm text-zinc-400" data-testid="campaigns-empty">
            Nessuna campagna per i filtri selezionati
          </div>
        )}
        {campaigns.map((c) => {
          const s = c.stats || {};
          return (
            <div key={c.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`campaign-${c.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-zinc-900">{c.name}</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-zinc-100 text-zinc-600 border-zinc-300 uppercase">
                      {CHANNELS.find((x) => x.key === c.channel)?.label || c.channel}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLE[c.status] || STATUS_STYLE.bozza}`}>
                      {(STATUSES.find((x) => x.key === c.status)?.label || c.status).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Creata il {fmtDate(c.created_at)} · {c.recipients_count || 0} destinatari
                    {c.scheduled_at && c.status === "programmata" ? ` · invio ${c.scheduled_at.replace("T", " ")}` : ""}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-5 gap-1 mt-3 text-center border-t border-zinc-100 pt-3">
                {[["Inviati", s.sent], ["Consegnati", s.delivered], ["Aperture", (s.opened || 0) + (s.read || 0)], ["Click", s.clicked], ["Risposte", s.replied]].map(([l, v]) => (
                  <div key={l}>
                    <div className="font-display font-bold text-zinc-900">{v || 0}</div>
                    <div className="text-[9px] uppercase tracking-wide text-zinc-400">{l}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5 mt-3 flex-wrap">
                <button onClick={() => setWizard({ campaign: c })} data-testid={`open-${c.id}`}
                  className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
                  <PencilSimple size={13} weight="bold" /> {["bozza", "programmata", "annullata"].includes(c.status) ? "Apri" : "Dettagli"}
                </button>
                <button onClick={() => setStatsFor(c)} data-testid={`cstats-${c.id}`}
                  className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                  <ChartBar size={13} weight="bold" /> Statistiche
                </button>
                {canEdit && (
                  <>
                    <button onClick={() => duplicate(c.id)} data-testid={`cduplicate-${c.id}`}
                      className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                      <Copy size={13} /> Duplica
                    </button>
                    {["programmata", "in_corso"].includes(c.status) && (
                      <button onClick={() => annulla(c.id)} data-testid={`cannulla-${c.id}`}
                        className="h-9 px-3 rounded-md bg-white border border-amber-300 text-xs font-semibold text-amber-700 flex items-center gap-1.5">
                        <Prohibit size={13} /> Annulla
                      </button>
                    )}
                    <button onClick={() => remove(c.id)} data-testid={`cdelete-${c.id}`}
                      className="h-9 px-3 rounded-md bg-white border border-rose-200 text-xs font-semibold text-rose-600 flex items-center gap-1.5">
                      <Trash size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {wizard && (
        <CampaignWizard campaign={wizard.campaign} onClose={() => setWizard(null)} onSaved={load} />
      )}
      {statsFor && <CampaignStats campaign={statsFor} onClose={() => setStatsFor(null)} />}
      {showTemplates && (
        <div className="fixed inset-0 z-[60] bg-black/40 overflow-y-auto p-4" onClick={() => setShowTemplates(false)}>
          <div className="max-w-2xl mx-auto my-8 bg-zinc-50 rounded-xl p-5" onClick={(e) => e.stopPropagation()}
            data-testid="wa-tpl-page-modal">
            <div className="flex justify-end mb-1">
              <button onClick={() => setShowTemplates(false)} data-testid="wa-tpl-page-close"
                className="h-8 w-8 rounded-md flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200 transition">
                <X size={16} weight="bold" />
              </button>
            </div>
            <WaTemplates />
          </div>
        </div>
      )}
    </div>
  );
}
